import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { getParametres, updateParametres, getCategories, addCategory, deleteCategory } from '../services/api'
import { useToast } from '../components/ToastProvider'
import { useConfirm } from '../components/ConfirmProvider'
import { categoryIcon } from '../utils/categoryIcon'
import { useTheme } from '../hooks/useTheme'
import Icon from '../components/Icon'

const currencies = {
  'Europe': ['EUR', 'GBP', 'CHF', 'SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'HUF', 'RON'],
  'Amérique': ['USD', 'CAD', 'MXN', 'BRL', 'ARS', 'CLP', 'COP'],
  'Asie': ['JPY', 'CNY', 'KRW', 'INR', 'SGD', 'HKD', 'TWD', 'THB', 'MYR', 'IDR', 'PHP', 'VND'],
  'Afrique': ['ZAR', 'EGP', 'NGN', 'KES', 'GHS', 'MAD', 'TND', 'XOF'],
  'Océanie': ['AUD', 'NZD']
}

const EMPTY_FORM = {
  decouvert_autorise: 0,
  montant_decouvert: '',
  montant_seuil: '',
  montant_minimum: '',
  montant_plafond: '',
  devise: 'EUR'
}

const SEUIL_FIELDS = [
  {
    key: 'montant_seuil',
    label: 'Seuil d’alerte',
    hint: 'Vous êtes prévenu lorsque le solde passe sous ce montant.',
  },
  {
    key: 'montant_minimum',
    label: 'Solde minimum',
    hint: 'Le plancher que vous souhaitez ne jamais franchir.',
  },
  {
    key: 'montant_plafond',
    label: 'Plafond',
    hint: 'Le solde au-delà duquel votre objectif est atteint.',
  },
]

export default function Parametres({ active = true }) {
  const [formData, setFormData] = useState(EMPTY_FORM)
  const [initialForm, setInitialForm] = useState(EMPTY_FORM)
  const [errors, setErrors] = useState({})
  const [categories, setCategories] = useState([])
  const [newCategory, setNewCategory] = useState('')
  const [addingCategory, setAddingCategory] = useState(false)
  const [deletingCategory, setDeletingCategory] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const hasLoadedRef = useRef(false)
  const toast = useToast()
  const confirm = useConfirm()
  const { theme, setTheme } = useTheme()

  const loadData = useCallback(async () => {
    try {
      if (!hasLoadedRef.current) setLoading(true)
      const [paramsData, catsData] = await Promise.all([
        getParametres().catch(err => {
          console.error('Erreur lors du chargement des paramètres:', err)
          return { devise: 'EUR', decouvert_autorise: 0 }
        }),
        getCategories().catch(err => {
          console.error('Erreur lors du chargement des catégories:', err)
          return []
        })
      ])

      if (paramsData) {
        const loaded = {
          decouvert_autorise: paramsData.decouvert_autorise || 0,
          montant_decouvert: paramsData.montant_decouvert && paramsData.montant_decouvert !== 0 ? String(paramsData.montant_decouvert) : '',
          montant_seuil: paramsData.montant_seuil && paramsData.montant_seuil !== 0 ? String(paramsData.montant_seuil) : '',
          montant_minimum: paramsData.montant_minimum && paramsData.montant_minimum !== 0 ? String(paramsData.montant_minimum) : '',
          montant_plafond: paramsData.montant_plafond && paramsData.montant_plafond !== 0 ? String(paramsData.montant_plafond) : '',
          devise: paramsData.devise || 'EUR'
        }
        setFormData(loaded)
        // Référence pour détecter les modifications non enregistrées.
        setInitialForm(loaded)
      }
      setCategories(catsData || [])
      hasLoadedRef.current = true
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error)
      toast.error('Erreur lors du chargement des paramètres')
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  const isDirty = useMemo(
    () => SEUIL_FIELDS.some(({ key }) => formData[key] !== initialForm[key]) || formData.devise !== initialForm.devise,
    [formData, initialForm]
  )

  // Prévenir avant de perdre une saisie en fermant l'onglet.
  useEffect(() => {
    if (!isDirty) return undefined
    const onBeforeUnload = (e) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [isDirty])

  const setField = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }))
    setErrors((current) => (current[field] ? { ...current, [field]: undefined } : current))
  }

  /** Les seuils sont facultatifs, mais s'ils sont renseignés ils doivent être des nombres positifs cohérents. */
  const validate = () => {
    const nextErrors = {}
    const parsed = {}
    SEUIL_FIELDS.forEach(({ key, label }) => {
      const raw = formData[key]
      if (raw === '' || raw == null) return
      const value = parseFloat(raw)
      if (Number.isNaN(value)) {
        nextErrors[key] = `${label} : saisissez un nombre`
      } else if (value < 0) {
        nextErrors[key] = `${label} ne peut pas être négatif`
      } else if (value > 999999999.99) {
        nextErrors[key] = `${label} est trop élevé`
      } else {
        parsed[key] = value
      }
    })

    // Un plafond sous le seuil d'alerte rend les jauges illisibles.
    if (parsed.montant_plafond != null && parsed.montant_seuil != null && parsed.montant_plafond < parsed.montant_seuil) {
      nextErrors.montant_plafond = 'Le plafond doit être supérieur au seuil d’alerte'
    }
    if (parsed.montant_seuil != null && parsed.montant_minimum != null && parsed.montant_seuil < parsed.montant_minimum) {
      nextErrors.montant_seuil = 'Le seuil d’alerte doit être supérieur au solde minimum'
    }

    setErrors(nextErrors)
    return nextErrors
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (saving) return

    const nextErrors = validate()
    const firstError = Object.keys(nextErrors)[0]
    if (firstError) {
      document.getElementById(`parametres-${firstError}`)?.focus()
      return
    }

    const deviseChanged = initialForm.devise !== formData.devise

    // Changer de devise réécrit tous les montants enregistrés : cela mérite une
    // confirmation explicite, pas une simple mention sous la liste déroulante.
    if (deviseChanged) {
      const confirmed = await confirm({
        title: 'Convertir tous vos montants ?',
        description: 'Opérations, seuils et alertes seront recalculés au taux en vigueur dans l’application.',
        confirmLabel: 'Convertir',
        danger: true,
        details: [
          { label: 'Devise actuelle', value: initialForm.devise },
          { label: 'Nouvelle devise', value: formData.devise },
        ],
        warning: 'Cette conversion s’applique à l’historique complet et n’est pas annulable automatiquement.',
      })
      if (!confirmed) return
    }

    try {
      setSaving(true)
      // Découvert = cumul des découverts des comptes (plus de paramètre global).
      const dataToSend = {
        ...formData,
        decouvert_autorise: 0,
        montant_decouvert: 0,
        montant_seuil: formData.montant_seuil === '' ? '' : parseFloat(formData.montant_seuil),
        montant_minimum: formData.montant_minimum === '' ? '' : parseFloat(formData.montant_minimum),
        montant_plafond: formData.montant_plafond === '' ? '' : parseFloat(formData.montant_plafond),
      }

      await updateParametres(dataToSend)

      if (deviseChanged) {
        // Laisser au serveur le temps de terminer la conversion avant de rafraîchir.
        await new Promise(resolve => setTimeout(resolve, 1000))
        window.dispatchEvent(new Event('currencyChanged'))
      }
      window.dispatchEvent(new Event('paramsChanged'))

      await loadData()
      toast.success(
        deviseChanged
          ? `Montants convertis en ${dataToSend.devise}`
          : 'Paramètres enregistrés'
      )
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error)
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Erreur lors de l’enregistrement')
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    setFormData(initialForm)
    setErrors({})
  }

  const categoryNames = useMemo(
    () => categories.map((cat) => (typeof cat === 'object' ? cat.nom : cat)),
    [categories]
  )

  const handleAddCategory = async (e) => {
    e.preventDefault()
    const nom = newCategory.trim()
    if (!nom || addingCategory) return

    // Vérifier le doublon avant l'aller-retour réseau.
    if (categoryNames.some((name) => name.toLowerCase() === nom.toLowerCase())) {
      toast.info(`La catégorie « ${nom} » existe déjà`)
      return
    }

    setAddingCategory(true)
    try {
      await addCategory({ nom })
      setNewCategory('')
      await loadData()
      window.dispatchEvent(new Event('categoriesChanged'))
      toast.success(`Catégorie « ${nom} » ajoutée`)
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la catégorie:', error)
      toast.error(error.response?.data?.message || 'Erreur lors de l’ajout de la catégorie')
    } finally {
      setAddingCategory(false)
    }
  }

  const handleDeleteCategory = async (nom) => {
    if (deletingCategory) return
    const confirmed = await confirm({
      title: 'Supprimer cette catégorie ?',
      description: 'Une catégorie utilisée par des opérations existantes ne peut pas être supprimée.',
      danger: true,
      confirmLabel: 'Supprimer',
      details: [{ label: 'Catégorie', value: nom }],
    })
    if (!confirmed) return

    setDeletingCategory(nom)
    try {
      await deleteCategory(nom)
      await loadData()
      window.dispatchEvent(new Event('categoriesChanged'))
      toast.success(`Catégorie « ${nom} » supprimée`)
    } catch (error) {
      console.error('Erreur lors de la suppression de la catégorie:', error)
      toast.error(error.response?.data?.message || 'Erreur lors de la suppression de la catégorie')
    } finally {
      setDeletingCategory(null)
    }
  }

  const fieldClass = (field) => `input tnum text-right ${errors[field] ? 'input-invalid' : ''}`

  const THEMES = [
    { key: 'light', label: 'Clair', icon: 'sun' },
    { key: 'dark', label: 'Sombre', icon: 'moon' },
    { key: 'system', label: 'Système', icon: 'settings' },
  ]

  return (
    <div className="app-page pb-28">
      <div className="app-page-header">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="app-page-title">Paramètres</h1>
            {loading && (
              <span className="flex items-center gap-1.5 text-sm text-ink-muted" role="status">
                <Icon name="refresh" size={14} className="animate-spin" />
                Chargement…
              </span>
            )}
          </div>
          <p className="app-page-subtitle">
            Seuils d’alerte, devise, apparence et catégories.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} noValidate className="space-y-6 sm:space-y-8">
        {/* Seuils */}
        <section className="card" aria-label="Seuils et limites">
          <div className="card-header">
            <div>
              <h2 className="card-title">Seuils et limites</h2>
              <p className="mt-0.5 text-sm text-ink-muted">
                Laissez un champ vide pour désactiver la jauge correspondante.
              </p>
            </div>
          </div>
          <div className="grid gap-5 p-5 sm:grid-cols-2 lg:grid-cols-3">
            {SEUIL_FIELDS.map(({ key, label, hint }) => (
              <div key={key} className="field">
                <label htmlFor={`parametres-${key}`} className="field-label">
                  {label} <span className="font-normal text-ink-muted">({formData.devise})</span>
                </label>
                <input
                  id={`parametres-${key}`}
                  type="number"
                  step="0.01"
                  min="0"
                  inputMode="decimal"
                  placeholder="—"
                  value={formData[key]}
                  onChange={(e) => setField(key, e.target.value)}
                  aria-invalid={!!errors[key]}
                  aria-describedby={errors[key] ? `parametres-${key}-error` : undefined}
                  className={fieldClass(key)}
                />
                {errors[key] ? (
                  <p id={`parametres-${key}-error`} role="alert" className="field-error">
                    {errors[key]}
                  </p>
                ) : (
                  <p className="field-hint">{hint}</p>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Devise */}
        <section className="card" aria-label="Devise">
          <div className="card-header">
            <h2 className="card-title">Devise</h2>
          </div>
          <div className="p-5">
            <div className="field max-w-xs">
              <label htmlFor="parametres-devise" className="field-label">Devise des montants</label>
              <select
                id="parametres-devise"
                value={formData.devise}
                onChange={(e) => setField('devise', e.target.value)}
                className="select"
              >
                {Object.entries(currencies).map(([region, codes]) => (
                  <optgroup key={region} label={region}>
                    {codes.map((code) => (
                      <option key={code} value={code}>{code}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            {formData.devise !== initialForm.devise ? (
              <div className="callout-warning mt-4">
                <Icon name="alert" size={18} className="mt-0.5 shrink-0" />
                <p>
                  À l’enregistrement, tous vos montants seront convertis de{' '}
                  <strong>{initialForm.devise}</strong> vers <strong>{formData.devise}</strong>.
                  Une confirmation vous sera demandée.
                </p>
              </div>
            ) : (
              <p className="mt-3 text-sm text-ink-muted">
                Changer la devise convertit l’ensemble des montants déjà enregistrés.
              </p>
            )}
          </div>
        </section>
      </form>

      {/* Apparence — hors du formulaire : le thème s'applique immédiatement et
          n'a rien à voir avec l'enregistrement des seuils. */}
      <section className="card" aria-label="Apparence">
        <div className="card-header">
          <div>
            <h2 className="card-title">Apparence</h2>
            <p className="mt-0.5 text-sm text-ink-muted">
              Appliqué immédiatement, sur cet appareil uniquement.
            </p>
          </div>
        </div>
        <div className="p-5">
          <div className="grid gap-2 sm:max-w-md sm:grid-cols-3">
            {THEMES.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTheme(t.key)}
                aria-pressed={theme === t.key}
                className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2.5 text-base font-medium transition-colors duration-fast ${
                  theme === t.key
                    ? 'border-accent bg-accent-subtle text-accent-on-subtle'
                    : 'border-line-strong bg-surface text-ink-secondary hover:bg-surface-hover'
                }`}
              >
                <Icon name={t.icon} size={16} />
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Catégories - Section indépendante */}
      <section className="card" aria-label="Catégories">
        <div className="card-header">
          <h2 className="card-title">Catégories</h2>
          <span className="badge-neutral">
            {categories.length} catégorie{categories.length > 1 ? 's' : ''}
          </span>
        </div>

        <div className="p-5">
          <form onSubmit={handleAddCategory} className="flex flex-col gap-2 sm:flex-row">
            <input
              type="text"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="Nouvelle catégorie"
              maxLength={100}
              aria-label="Nom de la nouvelle catégorie"
              className="input flex-1"
            />
            <button
              type="submit"
              disabled={!newCategory.trim() || addingCategory}
              className="btn-primary sm:w-auto"
            >
              {addingCategory ? (
                <Icon name="refresh" size={16} className="animate-spin" />
              ) : (
                <Icon name="plus" size={16} />
              )}
              {addingCategory ? 'Ajout…' : 'Ajouter'}
            </button>
          </form>

          {categories.length === 0 ? (
            <p className="py-8 text-center text-base text-ink-muted">
              Aucune catégorie pour le moment. Elles sont aussi créées automatiquement lors de la
              saisie d’une opération.
            </p>
          ) : (
            <ul className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {categories.map((cat) => {
                const categoryName = typeof cat === 'object' ? cat.nom : cat
                const isDeleting = deletingCategory === categoryName
                return (
                  <li
                    key={categoryName}
                    className="group flex items-center gap-2.5 rounded-md border border-line bg-surface px-3 py-2"
                  >
                    <Icon
                      name={categoryIcon(categoryName)}
                      size={16}
                      className="shrink-0 text-ink-muted"
                    />
                    <span className="min-w-0 flex-1 truncate text-base text-ink" title={categoryName}>
                      {categoryName}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleDeleteCategory(categoryName)}
                      disabled={isDeleting || !!deletingCategory}
                      className="btn-icon h-7 w-7 shrink-0 opacity-0 transition-opacity duration-fast hover:bg-negative-subtle hover:text-negative focus-visible:opacity-100 group-hover:opacity-100 disabled:opacity-100 max-sm:opacity-100"
                      title="Supprimer la catégorie"
                      aria-label={`Supprimer la catégorie ${categoryName}`}
                    >
                      <Icon
                        name={isDeleting ? 'refresh' : 'trash'}
                        size={15}
                        className={isDeleting ? 'animate-spin' : ''}
                      />
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </section>

      {/* Barre d'enregistrement : collée en bas tant qu'il reste des
          modifications, pour qu'on ne quitte pas la page en croyant avoir
          sauvegardé. */}
      {isDirty && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface px-4 py-3 shadow-lg sm:px-6">
          <div className="mx-auto flex max-w-[1200px] flex-wrap items-center gap-3">
            <span className="flex items-center gap-2 text-base text-ink-secondary">
              <span className="dot text-warning" aria-hidden="true" />
              Modifications non enregistrées
            </span>
            <div className="ml-auto flex gap-2">
              <button
                type="button"
                onClick={handleReset}
                disabled={saving}
                className="btn-secondary"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                className="btn-primary"
              >
                {saving && <Icon name="refresh" size={16} className="animate-spin" />}
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
